/**
 * featureDetection.js
 *
 * Unified feature detection system for config-driven architecture.
 * Replaces hardcoded ancestry/community/subclass checks throughout the codebase.
 *
 * Part of Issue #4 Architecture Refactor (Phase 11A)
 * @see docs/technical/issue4-architecture-refactor.md
 */

/**
 * Normalize ancestry string for comparison
 * Handles mixed ancestry patterns like "ribbet/firbolg"
 * @param {string|string[]} ancestry - Character ancestry
 * @returns {string[]} Array of normalized ancestry names
 */
export const normalizeAncestry = (ancestry) => {
  if (!ancestry) return [];

  // Handle array of ancestries (mixed ancestry)
  if (Array.isArray(ancestry)) {
    return ancestry.flatMap(a => {
      if (!a) return [];
      // Strip common prefixes and handle mixed patterns
      return a.toLowerCase().split('/').map(part =>
        part.trim()
          .replace(/^core_ancestry_/, '')
          .replace(/^ancestry_/, '')
      );
    });
  }

  // Handle string ancestry (with potential mixed pattern like "katari/halfling")
  return ancestry.toLowerCase().split('/').map(a => {
    return a.trim()
      .replace(/^core_ancestry_/, '')
      .replace(/^ancestry_/, '');
  });
};

/**
 * Check if character has a specific ancestry (including mixed)
 * @param {Object} character - Character object
 * @param {string} ancestryKey - Ancestry to check (e.g., 'firbolg', 'orc')
 * @returns {boolean}
 */
export const hasAncestry = (character, ancestryKey) => {
  if (!character || !ancestryKey) return false;

  const ancestries = normalizeAncestry(character.ancestry);
  const key = ancestryKey.toLowerCase();

  // Check primary ancestry
  if (ancestries.includes(key)) return true;

  // Check mixed ancestry array if present
  if (character.mixedAncestry?.some(a => a?.toLowerCase() === key)) return true;

  return false;
};

/**
 * Check if character has a specific community
 * @param {Object} character - Character object
 * @param {string} communityKey - Community to check (e.g., 'seaborne', 'orderborne')
 * @returns {boolean}
 */
export const hasCommunity = (character, communityKey) => {
  if (!character || !communityKey) return false;
  return character.community?.toLowerCase() === communityKey.toLowerCase();
};

/**
 * Check if character has a specific class
 * @param {Object} character - Character object
 * @param {string} classKey - Class to check (e.g., 'sorcerer', 'wizard', 'ranger')
 * @returns {boolean}
 */
export const hasClass = (character, classKey) => {
  if (!character || !classKey) return false;
  return character.class?.toLowerCase() === classKey.toLowerCase();
};

/**
 * Check if character has a specific subclass
 * @param {Object} character - Character object
 * @param {string} subclassKey - Subclass to check (e.g., 'school-of-knowledge')
 * @returns {boolean}
 */
export const hasSubclass = (character, subclassKey) => {
  if (!character || !subclassKey) return false;

  const charSubclass = character.subclass?.toLowerCase().replace(/\s+/g, '-');
  const key = subclassKey.toLowerCase().replace(/\s+/g, '-');

  return charSubclass === key;
};

/**
 * Check if character has a specific subclass feature (specialization or mastery)
 * @param {Object} character - Character object
 * @param {string} featureName - Feature name to check (e.g., 'Fueled By Fear')
 * @param {string} tier - Optional tier to check ('specialization' or 'mastery')
 * @returns {boolean}
 */
export const hasSubclassFeature = (character, featureName, tier = null) => {
  if (!character || !featureName) return false;

  const features = character.subclassFeatures || {};
  const name = featureName.toLowerCase();

  if (tier === 'specialization') {
    return features.specializations?.some(f => f.toLowerCase() === name) || false;
  }
  if (tier === 'mastery') {
    return features.masteries?.some(f => f.toLowerCase() === name) || false;
  }

  // Check both if no tier specified
  const inSpec = features.specializations?.some(f => f.toLowerCase() === name) || false;
  const inMast = features.masteries?.some(f => f.toLowerCase() === name) || false;

  return inSpec || inMast;
};

/**
 * Get the active feature state key mapping
 * Maps feature IDs to character state keys
 */
export const FEATURE_STATE_KEYS = {
  // Subclass features
  'adept': 'useAdept',
  'ruthless-predator': 'useRuthlessPredator',
  'apex-predator': 'useApexPredator',
  'rise-to-the-challenge': 'useRiseToChallenge',

  // Ancestry features
  'tusks': 'useTusks',
  'faun-kick': 'useFaunKick',
  'fearless': 'useFearless',
  'charge': 'useCharge',
  'retract': 'isRetracted',

  // Community features
  'orderborne-d20': 'useOrderborneD20',
};

/**
 * Check if a feature toggle is currently active on the character
 * @param {Object} character - Character object
 * @param {string} featureId - Feature ID (e.g., 'adept', 'tusks')
 * @returns {boolean}
 */
export const isFeatureActive = (character, featureId) => {
  if (!character || !featureId) return false;

  const stateKey = FEATURE_STATE_KEYS[featureId];
  if (!stateKey) return false;

  return character[stateKey] === true;
};

/**
 * Check if character meets the source requirement for a config
 * @param {Object} character - Character object
 * @param {string} source - Source type ('ancestry', 'community', 'subclass', 'domain-card')
 * @param {string} sourceId - Specific source ID
 * @returns {boolean}
 */
export const characterMatchesSource = (character, source, sourceId) => {
  if (!character || !source || !sourceId) return false;

  switch (source) {
    case 'ancestry':
      return hasAncestry(character, sourceId);
    case 'class':
      return hasClass(character, sourceId);
    case 'community':
      return hasCommunity(character, sourceId);
    case 'subclass':
      return hasSubclass(character, sourceId);
    case 'domain-card':
      // Delegate to cardFeatureDetection.js when available
      return character.loadout?.some(c =>
        c.toLowerCase().includes(sourceId.toLowerCase())
      ) || false;
    default:
      return false;
  }
};

/**
 * Get all active features for a character from a config object
 * @param {Object} character - Character object
 * @param {Object} configMap - Config object keyed by feature ID (e.g., SUBCLASS_ROLL_MODIFIERS)
 * @returns {Array<{featureId: string, config: Object}>}
 */
export const getActiveFeatures = (character, configMap) => {
  if (!character || !configMap) return [];

  const results = [];

  for (const [featureId, config] of Object.entries(configMap)) {
    // Check if config has source requirements
    if (config.source && config.sourceId) {
      if (!characterMatchesSource(character, config.source, config.sourceId)) {
        continue;
      }
    }

    // Check if feature has a condition function
    if (config.condition && typeof config.condition === 'function') {
      if (!config.condition(character)) {
        continue;
      }
    }

    // Check if feature toggle is active (if applicable)
    if (FEATURE_STATE_KEYS[featureId]) {
      if (!isFeatureActive(character, featureId)) {
        continue;
      }
    }

    results.push({ featureId, config });
  }

  return results;
};

/**
 * Find a feature config that provides a specific capability
 * @param {Object} character - Character object
 * @param {string} capability - Capability to look for (e.g., 'experienceModifier', 'bonusMeleeDamage')
 * @param {Object} configMaps - Object containing config maps to search (e.g., { subclass: SUBCLASS_ROLL_MODIFIERS })
 * @returns {Object|null} - { source: string, featureId: string, config: Object } or null
 */
export const getFeatureWithCapability = (character, capability, configMaps) => {
  if (!character || !capability || !configMaps) return null;

  for (const [source, configMap] of Object.entries(configMaps)) {
    if (!configMap) continue;

    for (const [featureId, config] of Object.entries(configMap)) {
      // Check if config has the capability
      if (config[capability] === undefined) continue;

      // Check source requirements
      if (config.source && config.sourceId) {
        if (!characterMatchesSource(character, config.source, config.sourceId)) {
          continue;
        }
      }

      // Check condition
      if (config.condition && typeof config.condition === 'function') {
        if (!config.condition(character)) {
          continue;
        }
      }

      return { source, featureId, config };
    }
  }

  return null;
};

/**
 * Check if character has any feature with a specific capability that's currently active
 * @param {Object} character - Character object
 * @param {string} capability - Capability to check
 * @param {Object} configMaps - Config maps to search
 * @returns {boolean}
 */
export const hasActiveCapability = (character, capability, configMaps) => {
  const feature = getFeatureWithCapability(character, capability, configMaps);
  if (!feature) return false;

  // If feature requires toggle, check if active
  if (FEATURE_STATE_KEYS[feature.featureId]) {
    return isFeatureActive(character, feature.featureId);
  }

  return true;
};

// ============================================================================
// ANCESTRY-FEATURE MAPPING (Phase 16)
// ============================================================================
// Maps feature names to their valid ancestries for config-driven lookup.
// Replaces hardcoded checks in AbilitiesPanel.js:433-439

/**
 * Mapping of ancestry features to their source ancestries
 * Used for config key generation: `{ancestry}:{feature-slug}`
 */
const ANCESTRY_FEATURE_MAPPING = {
  // Clank
  'Efficient': ['clank'],
  'Purposeful Design': ['clank'],
  // Drakona
  'Scales': ['drakona'],
  'Elemental Breath': ['drakona'],
  // Dwarf
  'Thick Skin': ['dwarf'],
  'Increased Fortitude': ['dwarf'],
  // Elf
  'Celestial Trance': ['elf'],
  'Quick Reactions': ['elf'],
  // Faerie
  'Luckbender': ['faerie'],
  'Wings': ['faerie'],
  // Faun
  'Caprine Leap': ['faun'],
  'Kick': ['faun'],
  // Firbolg
  'Charge': ['firbolg'],
  'Unshakable': ['firbolg'],
  // Fungril
  'Fungril Network': ['fungril'],
  'Death Connection': ['fungril'],
  // Galapa
  'Retract': ['galapa'],
  'Shell': ['galapa'],
  // Giant
  'Reach': ['giant'],
  'Endurance': ['giant'],
  // Goblin
  'Surefooted': ['goblin'],
  'Danger Sense': ['goblin'],
  // Halfling
  'Internal Compass': ['halfling'],
  'Luckbringer': ['halfling'],
  // Human
  'High Stamina': ['human'],
  'Adaptability': ['human'],
  // Infernis
  'Dread Visage': ['infernis'],
  'Fearless': ['infernis'],
  // Katari
  'Feline Instincts': ['katari'],
  'Retracting Claws': ['katari'],
  // Orc
  'Sturdy': ['orc'],
  'Tusks': ['orc'],
  // Ribbet
  'Long Tongue': ['ribbet'],
  'Amphibious': ['ribbet'],
  // Simiah
  'Nimble': ['simiah'],
  'Natural Climber': ['simiah'],
};

/**
 * Get the ancestry source for a feature name
 * Used by AbilitiesPanel to determine config key for AbilityRenderer
 *
 * @param {Object} character - Character object with ancestry field
 * @param {string} featureName - Name of the ancestry feature
 * @returns {string} The matching ancestry from character, or first ancestry if no match
 *
 * @example
 * getAncestryForFeature({ ancestry: ['faerie', 'halfling'] }, 'Luckbender')
 * // Returns: 'faerie'
 */
export const getAncestryForFeature = (character, featureName) => {
  if (!character || !featureName) return null;

  const validAncestries = ANCESTRY_FEATURE_MAPPING[featureName] || [];

  // If character has array ancestry (mixed ancestry)
  if (Array.isArray(character.ancestry)) {
    // Find which of the character's ancestries matches the feature
    const match = character.ancestry.find(charAncestry => {
      if (!charAncestry) return false;
      const normalized = charAncestry.toLowerCase()
        .replace(/^core_ancestry_/, '')
        .replace(/^ancestry_/, '');
      return validAncestries.some(va => normalized.includes(va.toLowerCase()));
    });
    // Return matched ancestry or fall back to first
    return match || character.ancestry[0];
  }

  // Single ancestry - return as-is
  return character.ancestry;
};

/**
 * Check if a character actually has a specific ancestry feature selected.
 * Unlike hasAncestry(), this checks the ancestryFeatures array so mixed-ancestry
 * characters who didn't choose a particular feature won't match.
 *
 * @param {Object} character - Character object with ancestryFeatures array
 * @param {string} featureName - Exact feature name (e.g., 'Retracting Claws', 'Tusks')
 * @returns {boolean}
 */
export const hasAncestryFeature = (character, featureName) => {
  if (!character || !featureName) return false;
  const features = character.ancestryFeatures || [];
  return features.some(f => f?.name === featureName);
};
